import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faArrowRight, faArrowLeft, faSpinner, faPlus, faTrash, faWarehouse } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import customersService from '../../services/customersService';
import locationsService from '../../services/locationsService';
import productSizesService from '../../services/productSizesServices';
import productCatalogueService from '../../services/productCatalogueService';
import inquiriesService from '../../services/inquiriesService';
import warehouseService from '../../services/warehouseService';
import validateUtils from '../../utils/validate';
import SearchableSelect from '../common/SearchableSelect';
import { computeInquiryTotals, lineSubtotal } from '../../utils/inquiryTotals';
import { formatCurrency } from '../../utils/format';

import AddLocationModal from '../locations/AddLocationModal';
import CustomerWarehouseForm from '../customers/CustomerWarehouseForm';

// defaultNewCustomer will be created inside the component so it can use localized strings

// Small helper to format price-like numbers
const sanitizeNumber = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const s = String(v).replace(/[^0-9.]/g, '');
  return s;
};

// Helper to create an empty item. `length` is populated internally from the
// picked product's standard_length; the UI no longer exposes a Length input.
const emptyItem = () => ({ product_catalogue_id: '', product_size_id: '', units: 'kg', length: '', quantity: '', quantity_in_pieces: '', price: '', remark: '', gst_rate: '' });

// product_weight / standard_length come back as strings ("14.500") or null.
const pickProductWeight = (product) => parseFloat(
  product?.product_weight ?? product?.weight ?? 0
) || 0;
const pickStandardLength = (product) => parseFloat(
  product?.standard_length ?? product?.length ?? product?.piece_length ?? 0
) || 0;

// Resolve a product size's Sub Category (product catalogue) id.
//
// The /product-size LIST serializer does NOT include product_catalogue_id — only
// the detail endpoint (/product-size/{id}) does. The list DOES carry
// `product_name`, which is exactly the catalogue's `product_name`
// (e.g. "72104900=GP/GI SHEET [0.1mm to 3mm]"), so we fall back to joining on
// that against the loaded Sub Category list.
const resolveCatalogueId = (product, subCategories = []) => {
  if (!product) return '';
  const direct = product.product_catalogue_id
    ?? product.catalogue_id
    ?? product.product_catalogue?.id
    ?? product.catalogue?.id;
  if (direct !== undefined && direct !== null && direct !== '') return String(direct);

  const name = product.product_name;
  if (!name) return '';
  const match = subCategories.find(sc => sc.product_name === name);
  return match ? String(match.id) : '';
};

// Unit classification. The persisted values are the keys of
// STRINGS.common.unitOptions — "kg" | "meters" | "mts" | "pieces". Legacy rows
// may also carry "m" (length) or "pcs" (count), so both are tolerated.
//   kg     → weight in kilograms
//   mts    → weight in metric tonnes (1 MTS = 1000 kg)
//   meters → length
//   pieces → a raw count; quantity IS the piece count
const isLengthUnit = (u) => u === 'm' || u === 'meters';
const isTonneUnit = (u) => u === 'mts';
const isPieceUnit = (u) => u === 'pieces' || u === 'pcs';

const KG_PER_TONNE = 1000;
const fmtQty = (n) => String(sanitizeNumber(n.toFixed(3)));

// Per-piece measure — unit-dependent:
//   meters      → product.standard_length (length per piece)
//   kg / mts    → product.product_weight (always quoted in kg per piece)
//   pieces      → not meaningful, blank
const computePerPiece = (item, products) => {
  const product = products.find(p => String(p.id) === String(item.product_size_id));
  if (!product) return '';
  if (isPieceUnit(item.units)) return '';
  if (isLengthUnit(item.units)) {
    const sLen = pickStandardLength(product);
    return sLen > 0 ? fmtQty(sLen) : '';
  }
  const w = pickProductWeight(product);
  return w > 0 ? fmtQty(w) : '';
};

// Approx Qty — read-only guidance; the user overrides via the Quantity input.
//   pieces → mirrors Pieces 1:1
//   meters → pieces × standard_length
//   kg     → pieces × product_weight
//   mts    → pieces × product_weight ÷ 1000
const computeApproxQty = (item, products) => {
  const product = products.find(p => String(p.id) === String(item.product_size_id));
  if (!product) return '';
  const pcs = parseFloat(item.quantity_in_pieces);
  if (!pcs || pcs <= 0) return '';

  if (isPieceUnit(item.units)) return fmtQty(pcs);

  if (isLengthUnit(item.units)) {
    const sLen = pickStandardLength(product);
    return sLen > 0 ? fmtQty(pcs * sLen) : '';
  }

  const w = pickProductWeight(product);
  if (w <= 0) return '';
  const kg = pcs * w;
  return isTonneUnit(item.units) ? fmtQty(kg / KG_PER_TONNE) : fmtQty(kg);
};

// Resolve the row's Sub Category id from either explicit state or, if empty,
// the currently-picked product's catalogue link (edit-flow hydration).
const effectiveSubCatId = (item, products, subCategories = []) => {
  if (item.product_catalogue_id) return String(item.product_catalogue_id);
  const product = products.find(p => String(p.id) === String(item.product_size_id));
  return resolveCatalogueId(product, subCategories);
};



// Helper to get tomorrow's date in YYYY-MM-DD format
const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const y = tomorrow.getFullYear();
  const m = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const d = String(tomorrow.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const InquiryForm = ({ isOpen, onClose, onSubmit, inquiry = null, inquiryId = null, loading = false, isConverting = false }) => {
  const { t } = useTranslation();
  const strings = t('inquiriesModule.inquiriesForm', { returnObjects: true });
  const fields = strings.fields;
  const REQUIRED = t('common.form.requiredIndicator');

  // short-circuit render only after hooks are declared
  const defaultNewCustomer = useMemo(() => ({
    owner_name: '',
    company_name: '',
    gst_number: '',
    contact_person: '',
    customer_type: fields?.customer?.customer_type?.placeholder,
    address_line1: '',
    state: '',
    city: '',
    country: t('locations.defaults.country'),
    zip_code: '',
    email: '',
    phone_number_1: '',
    tally_name: '',
    credit_period: 0,
    reference_party_name: '',
  }), [fields?.customer?.customer_type?.placeholder, t]);

  // mode: 'existing' or 'new'
  // When `inquiry` is not provided (adding a new inquiry) default to 'existing'
  const initialMode = 'existing';
  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState(0); // for new customer: 0 = customer info, 1 = warehouses, 2 = order info
  const [newCustomerWarehouses, setNewCustomerWarehouses] = useState([]);
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);

  // State for fetching inquiry data
  const [currentInquiry, setCurrentInquiry] = useState(inquiry);
  const [inquiryLoading, setInquiryLoading] = useState(false);
  // Guard ref to avoid duplicate fetches for the same inquiry id
  const lastFetchedIdRef = useRef(null);

  // Dropdown caches
  if (!window.inquiryFormCache) {
    window.inquiryFormCache = { customers: null, locations: null, products: null, loaded: false, loading: false };
  }
  const cache = window.inquiryFormCache;

  // Function to clear cache if needed (useful for development or when data changes)
  // eslint-disable-next-line no-unused-vars
  const clearCache = useCallback(() => {
    window.inquiryFormCache = { customers: null, locations: null, products: null, loaded: false, loading: false };
    setCustomers([]);
    setLocations([]);
    setProducts([]);
  }, []);

  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  // Per-item remarks are hidden by default to keep the row compact, but must
  // start visible whenever a line already carries one — otherwise editing an
  // inquiry would silently conceal text the user wrote earlier.
  const [showItemRemarks, setShowItemRemarks] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [dropdownsLoading, setDropdownsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const customerSearchRef = useRef(null);
  const customerActiveTermRef = useRef('');
  const [productSearchLoading, setProductSearchLoading] = useState(new Set());
  const productSearchTimers = useRef(new Map());
  const productActiveTermRef = useRef('');
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const supplierSearchRef = useRef(null);

  const [suppliers, setSuppliers] = useState([]);
  const [locationSearchLoading, setLocationSearchLoading] = useState(false);
  const locationSearchRef = useRef(null);
  const [warehouseSearchLoading, setWarehouseSearchLoading] = useState(false);
  const warehouseSearchRef = useRef(null);



  // Form state for order fields (shared)
  const [order, setOrder] = useState({
    customer_id: currentInquiry?.customer_id ?? '',
    location_id: currentInquiry?.location_id ?? '',
    freight: currentInquiry?.freight ?? '',
    freight_type: currentInquiry?.freight_type ?? '',
    freight_pricing_type: currentInquiry?.freight_pricing_type ?? 'fixed',
    expected_delivery_date: currentInquiry?.expected_delivery_date ?? getTomorrowDate(),
    include_gst: currentInquiry?.include_gst ?? currentInquiry?.gst_included ?? false,
    source_type: '',
    selected_warehouse_id: '',
    selected_supplier_id: '',
    loading_cost_per_kg: '',
    reference_party_name: currentInquiry?.reference_party_name ?? '',
    remark: currentInquiry?.remark ?? '',
    items: Array.isArray(currentInquiry?.items) && currentInquiry.items.length > 0 ?
      currentInquiry.items.map(i => ({
        product_catalogue_id: i.product_catalogue_id || i.catalogue_id || '',
        product_size_id: i.product_size_id || '',
        units: i.units || 'kg',
        length: i.length || '',
        quantity: i.quantity || '',
        quantity_in_pieces: i.quantity_in_pieces || i.pieces || i.quantity_per_pieces || '',
        price: i.price || '',
        // Carried so the form can preview GST without refetching the product.
        gst_rate: i.gst_rate ?? '',
        remark: i.remark || ''
      })) : [emptyItem()],
  });

  const formOrderRef = useRef(order);
  useEffect(() => {
    formOrderRef.current = order;
  }, [order]);

  // Reveal the per-item remark inputs whenever a loaded line already has one,
  // so editing an existing inquiry never hides text that is already there.
  useEffect(() => {
    if (!isOpen) return;
    const anyRemark = (order.items || []).some((it) => String(it?.remark || '').trim());
    if (anyRemark) setShowItemRemarks(true);
    // Only widens visibility — never flips back off while the user is editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentInquiry, inquiry]);

  // Load Sub Category (product catalogue) options once when the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await productCatalogueService.getAllProducts();
        if (cancelled) return;
        const list = Array.isArray(result?.data) ? result.data : (Array.isArray(result) ? result : []);
        setSubCategories(list);
      } catch (e) {
        if (!cancelled) setSubCategories([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  const [newCustomer, setNewCustomer] = useState(currentInquiry?.customer || defaultNewCustomer);
  const [errors, setErrors] = useState({});

  const handleCustomerSearch = useCallback((term) => {
    clearTimeout(customerSearchRef.current);
    customerActiveTermRef.current = term;
    if (!term.trim()) {
      setCustomerSearchLoading(false);
      setCustomers((prev) => {
        const base = cache.customers || [];
        const selectedId = String(formOrderRef.current?.customer_id || '');
        if (selectedId && !base.some((c) => String(c.id) === String(selectedId))) {
          const selected = prev.find((c) => String(c.id) === String(selectedId));
          return selected ? [selected, ...base] : base;
        }
        return base;
      });
      return;
    }
    setCustomerSearchLoading(true);
    customerSearchRef.current = setTimeout(async () => {
      try {
        const response = await customersService.getCustomers({ search: term, limit: 20, is_active: true });
        if (customerActiveTermRef.current !== term) return;
        const list = Array.isArray(response?.data) ? response.data : [];
        setCustomers((prev) => {
          // Always keep the currently selected customer in the list
          const selectedId = String(formOrderRef.current?.customer_id || '');
          if (selectedId) {
            const selected = prev.find((c) => String(c.id) === String(selectedId));
            if (selected && !list.some((c) => String(c.id) === String(selectedId))) {
              return [selected, ...list];
            }
          }
          return list;
        });
      } catch (e) {
        // ignore search errors silently
      } finally {
        if (customerActiveTermRef.current === term) setCustomerSearchLoading(false);
      }
    }, 400);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache]);

  // Fetch product sizes filtered by a Sub Category and merge them into the
  // shared `products` state so the Product dropdown for that row lists ALL
  // items in that sub category (server-side filter, not the initial 20).
  const loadProductsForSubCat = useCallback(async (idx, subCatId) => {
    if (!subCatId) return;
    setProductSearchLoading(prev => new Set([...prev, idx]));
    try {
      // force: bypass the 10-minute session cache so freshly-edited product
      // specs (product_weight / standard_length) show up immediately.
      const response = await productSizesService.getAllProductSizes(
        { product_catalogue_id: subCatId, limit: 100 },
        { force: true }
      );
      const raw = response?.data;
      const data = Array.isArray(raw) ? raw : (Array.isArray(raw?.items) ? raw.items : []);
      setProducts(prev => {
        const map = new Map((prev || []).map(p => [String(p.id), p]));
        data.forEach(p => map.set(String(p.id), p));
        return Array.from(map.values());
      });
      if (cache.products) {
        const existingIds = new Set(cache.products.map(p => String(p.id)));
        const fresh = data.filter(p => !existingIds.has(String(p.id)));
        if (fresh.length > 0) cache.products = [...cache.products, ...fresh];
      }
    } catch (e) {
      // ignore silently — dropdown will just show whatever is already loaded
    } finally {
      setProductSearchLoading(prev => { const next = new Set(prev); next.delete(idx); return next; });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache]);

  // The /product-size LIST serializer is slim and can be served from a stale
  // session cache. On selection, pull the authoritative record from
  // /product-size/{id} — it carries product_catalogue_id, standard_length and
  // product_weight — and merge it over the cached list row.
  const hydrateProductDetail = useCallback(async (idx, productSizeId) => {
    if (!productSizeId) return;
    try {
      const response = await productSizesService.getProductSizeById(productSizeId, { force: true });
      const detail = response?.data ?? response;
      if (!detail || !detail.id) return;

      setProducts(prev => {
        const map = new Map((prev || []).map(p => [String(p.id), p]));
        map.set(String(detail.id), { ...(map.get(String(detail.id)) || {}), ...detail });
        return Array.from(map.values());
      });

      // Re-apply the spec-derived fields now that we have authoritative values.
      setOrder(prev => {
        const items = Array.isArray(prev.items) ? [...prev.items] : [];
        const row = items[idx];
        if (!row || String(row.product_size_id) !== String(detail.id)) return prev;
        const sLen = pickStandardLength(detail);
        const catId = resolveCatalogueId(detail, subCategories);
        items[idx] = {
          ...row,
          ...(sLen > 0 ? { length: String(sLen) } : {}),
          ...(catId ? { product_catalogue_id: catId } : {}),
          ...(detail.gst_rate !== undefined && detail.gst_rate !== null
            ? { gst_rate: detail.gst_rate }
            : {}),
        };
        return { ...prev, items };
      });
    } catch (e) {
      // non-fatal — the row keeps whatever the list response provided
    }
  }, [subCategories]);

  const handleProductSearch = useCallback((idx, term, subCatId) => {
    clearTimeout(productSearchTimers.current.get(idx));
    productActiveTermRef.current = term;
    if (!term.trim()) {
      setProductSearchLoading(prev => { const next = new Set(prev); next.delete(idx); return next; });
      setProducts(cache.products || []);
      return;
    }
    productSearchTimers.current.set(idx, setTimeout(async () => {
      setProductSearchLoading(prev => new Set([...prev, idx]));
      try {
        const params = { search: term, limit: 20 };
        if (subCatId) params.product_catalogue_id = subCatId;
        const response = await productSizesService.getAllProductSizes(params);
        if (productActiveTermRef.current !== term) return;
        const raw = response?.data;
        const data = Array.isArray(raw) ? raw : (Array.isArray(raw?.items) ? raw.items : []);
        if (cache.products) {
          const existingIds = new Set(cache.products.map(p => String(p.id)));
          const newProducts = data.filter(p => !existingIds.has(String(p.id)));
          if (newProducts.length > 0) {
            cache.products = [...cache.products, ...newProducts];
          }
        }
        setProducts(data);
      } catch (e) {
        // ignore search errors silently
      } finally {
        if (productActiveTermRef.current === term) setProductSearchLoading(prev => { const next = new Set(prev); next.delete(idx); return next; });
      }
    }, 400));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache]);

  const handleSupplierSearch = useCallback((term) => {
    clearTimeout(supplierSearchRef.current);
    if (!term.trim()) {
      setSupplierSearchLoading(false);
      setSuppliers((prev) => {
        const base = cache.suppliers || prev;
        const selectedId = String(formOrderRef.current?.selected_supplier_id || '');
        if (selectedId && !base.some((c) => String(c.id) === String(selectedId))) {
          const selected = prev.find((c) => String(c.id) === String(selectedId));
          return selected ? [selected, ...base] : base;
        }
        return base;
      });
      return;
    }
    setSupplierSearchLoading(true);
    supplierSearchRef.current = setTimeout(async () => {
      try {
        const response = await customersService.getCustomers({ search: term, customer_type: 'Supplier', limit: 20, is_active: true });
        const all = Array.isArray(response?.data) ? response.data : [];
        const list = all.filter(c => String(c.customer_type ?? c.type ?? c.customerType ?? '').toLowerCase() === 'supplier');
        setSuppliers((prev) => {
          const selectedId = String(formOrderRef.current?.selected_supplier_id || '');
          if (selectedId) {
            const selected = prev.find((c) => String(c.id) === String(selectedId));
            if (selected && !list.some((c) => String(c.id) === String(selectedId))) {
              return [selected, ...list];
            }
          }
          return list;
        });
      } catch (e) {
        // ignore search errors silently
      } finally {
        setSupplierSearchLoading(false);
      }
    }, 400);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache]);

  const handleLocationSearch = useCallback((term) => {
    if (locationSearchRef.current) clearTimeout(locationSearchRef.current);
    if (!term.trim()) {
      setLocationSearchLoading(false);
      setLocations((prev) => {
        const base = cache.locations || prev;
        const selectedId = String(formOrderRef.current?.location_id || '');
        if (selectedId && !base.some((c) => String(c.id) === String(selectedId))) {
          const selected = prev.find((c) => String(c.id) === String(selectedId));
          return selected ? [selected, ...base] : base;
        }
        return base;
      });
      return;
    }
    setLocationSearchLoading(true);
    locationSearchRef.current = setTimeout(async () => {
      try {
        const response = await locationsService.getLocations({ search: term, limit: 20 });
        const list = Array.isArray(response?.data) ? response.data : [];
        setLocations((prev) => {
          const selectedId = String(formOrderRef.current?.location_id || '');
          if (selectedId) {
            const selected = prev.find((c) => String(c.id) === String(selectedId));
            if (selected && !list.some((c) => String(c.id) === String(selectedId))) {
              return [selected, ...list];
            }
          }
          return list;
        });
      } catch (e) {
      } finally {
        setLocationSearchLoading(false);
      }
    }, 400);
  }, [cache]);

  const handleWarehouseSearch = useCallback((term) => {
    if (warehouseSearchRef.current) clearTimeout(warehouseSearchRef.current);
    if (!term.trim()) {
      setWarehouseSearchLoading(false);
      setWarehouses((prev) => {
        const base = cache.warehouses || prev;
        const selectedId = String(formOrderRef.current?.selected_warehouse_id || '');
        if (selectedId && !base.some((w) => String(w.id) === String(selectedId))) {
          const selected = prev.find((w) => String(w.id) === String(selectedId));
          return selected ? [selected, ...base] : base;
        }
        return base;
      });
      return;
    }
    setWarehouseSearchLoading(true);
    warehouseSearchRef.current = setTimeout(async () => {
      try {
        const response = await warehouseService.getAllWarehouses({ search: term, limit: 20 });
        const list = Array.isArray(response?.data) ? response.data : [];
        setWarehouses((prev) => {
          const selectedId = String(formOrderRef.current?.selected_warehouse_id || '');
          if (selectedId) {
            const selected = prev.find((w) => String(w.id) === String(selectedId));
            if (selected && !list.some((w) => String(w.id) === String(selectedId))) {
              return [selected, ...list];
            }
          }
          return list;
        });
      } catch (e) {
      } finally {
        setWarehouseSearchLoading(false);
      }
    }, 400);
  }, [cache]);

  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [locationAddInitialValue, setLocationAddInitialValue] = useState('');

  const handleAddLocationSuccess = (newLocation) => {
    setLocations(prev => {
        const hasIt = prev.some(l => l.id === newLocation.id);
        if (hasIt) return prev;
        return [...prev, newLocation];
    });
    if (cache.locations) {
        if (!cache.locations.some(l => l.id === newLocation.id)) {
            cache.locations = [...cache.locations, newLocation];
        }
    }
    setOrder(prev => ({ ...prev, location_id: newLocation.id }));
  };

  // Fetch inquiry data when inquiryId is provided but inquiry data is not
  const fetchInquiryData = useCallback(async (id) => {
    if (!id) return null;

    // If we've already initiated a fetch for this id, skip to avoid duplicate API calls
    if (lastFetchedIdRef.current === id) return null;

    lastFetchedIdRef.current = id;
    setInquiryLoading(true);
    try {
      const result = await inquiriesService.getInquiryById(id, { force: true });
      if (result && result.success && result.data) {
        setCurrentInquiry(result.data);
        return result.data;
      }
      console.error('Failed to fetch inquiry:', result && result.error);
      return null;
    } catch (error) {
      console.error('Error fetching inquiry:', error);
      return null;
    } finally {
      setInquiryLoading(false);
    }
  }, []);



  // Reset lastFetchedIdRef when modal is closed so subsequent opens can fetch new ids
  useEffect(() => {
    if (!isOpen) {
      lastFetchedIdRef.current = null;
      setCurrentInquiry(inquiry || null);
    }
  }, [isOpen, inquiry]);

  useEffect(() => {
    // initialize when open or inquiry changes
    // When editing an existing inquiry, do not allow switching to 'new' customer mode.
    const activeInquiry = currentInquiry || inquiry;

    // If we have an inquiryId but no inquiry data, fetch it
    if (isOpen && inquiryId && !inquiry && !currentInquiry) {
      fetchInquiryData(inquiryId).then((fetchedInquiry) => {
        if (fetchedInquiry) {
          // Update form state with fetched data
          setOrder({
            customer_id: fetchedInquiry?.customer_id ?? '',
            location_id: fetchedInquiry?.location_id ?? '',
            freight: fetchedInquiry?.freight ?? '',
            freight_type: fetchedInquiry?.freight_type ?? '',
            freight_pricing_type: fetchedInquiry?.freight_pricing_type ?? 'fixed',
            expected_delivery_date: fetchedInquiry?.expected_delivery_date ?? getTomorrowDate(),
            include_gst: fetchedInquiry?.include_gst ?? fetchedInquiry?.gst_included ?? false,
            remark: fetchedInquiry?.remark ?? '',
            items: Array.isArray(fetchedInquiry?.items) && fetchedInquiry.items.length > 0 ?
              fetchedInquiry.items.map(i => ({
                product_catalogue_id: i.product_catalogue_id || i.catalogue_id || '',
                product_size_id: i.product_size_id || '',
                product_name: i.product_name || i.product?.name || '',
                size_name: i.size_name || i.alias_name || '',
                units: i.units || 'kg',
                length: i.length || '',
                quantity: i.quantity || '',
                quantity_in_pieces: i.quantity_in_pieces || i.pieces || i.quantity_per_pieces || '',
                price: i.price || '',
                gst_rate: i.gst_rate ?? '',
                remark: i.remark || ''
              })) : [emptyItem()],
            // Conversion fields defaults
            source_type: '',
            loading_cost_per_kg: '',
            selected_warehouse_id: '',
            selected_supplier_id: '',
          });
          setNewCustomer(fetchedInquiry?.customer || defaultNewCustomer);
        }
      });
      return; // Don't continue with initialization if we're fetching
    }

    setMode(activeInquiry ? 'existing' : initialMode);
    setStep(0);
    setOrder({
      customer_id: activeInquiry?.customer_id ?? '',
      location_id: activeInquiry?.location_id ?? '',
      freight: activeInquiry?.freight ?? '',
      freight_type: activeInquiry?.freight_type ?? '',
      freight_pricing_type: activeInquiry?.freight_pricing_type ?? 'fixed',
      expected_delivery_date: activeInquiry?.expected_delivery_date ?? getTomorrowDate(),
      include_gst: activeInquiry?.include_gst ?? activeInquiry?.gst_included ?? false,
      reference_party_name: activeInquiry?.reference_party_name ?? '',
      remark: activeInquiry?.remark ?? '',
      items: Array.isArray(activeInquiry?.items) && activeInquiry.items.length > 0 ?
        activeInquiry.items.map(i => ({
          product_catalogue_id: i.product_catalogue_id || i.catalogue_id || '',
          product_size_id: i.product_size_id || '',
          product_name: i.product_name || i.product?.name || '',
          size_name: i.size_name || i.alias_name || '',
          units: i.units || 'kg',
          length: i.length || '',
          quantity: i.quantity || '',
          quantity_in_pieces: i.quantity_in_pieces || i.pieces || i.quantity_per_pieces || '',
          price: i.price || '',
          gst_rate: i.gst_rate ?? '',
          remark: i.remark || ''
        })) : [emptyItem()],
      // Conversion fields defaults
      loading_cost_per_kg: '',
      selected_warehouse_id: '',
      selected_supplier_id: '',
    });
    setNewCustomer(activeInquiry?.customer || defaultNewCustomer);
    setErrors({});
  }, [isOpen, inquiry, currentInquiry, inquiryId, defaultNewCustomer, initialMode, fetchInquiryData]);




  const loadDropdowns = useCallback(async () => {
    // If data is already cached and current state is empty, load from cache
    if (cache.loaded) {
      if (suppliers.length === 0 && cache.suppliers) setSuppliers(cache.suppliers);
      if (locations.length === 0 && cache.locations) setLocations(cache.locations);
      if (warehouses.length === 0 && cache.warehouses) setWarehouses(cache.warehouses);
      return;
    }

    // Prevent multiple simultaneous API calls
    if (cache.loading) return;

    cache.loading = true;
    setDropdownsLoading(true);

    try {
      console.log('Loading dropdown data...');

      // Call all APIs in parallel
      const [customersResponse, locationsResponse, productSizesResponse, warehousesResponse] = await Promise.all([
        customersService.getCustomers({ is_active: true }).catch((error) => {
          console.error('Error loading customers:', error);
          return { data: [] };
        }),
        locationsService.getLocations().catch((error) => {
          console.error('Error loading locations:', error);
          return { data: [] };
        }),
        // force: product specs (product_weight / standard_length) drive the
        // Per Piece and Approx Qty columns, so never render them from a stale
        // 10-minute session cache.
        productSizesService.getAllProductSizes({}, { force: true }).catch((error) => {
          console.error('Error loading product sizes:', error);
          return { data: [] };
        }),
        warehouseService.getAllWarehouses().catch((error) => {
          console.error('Error loading warehouses:', error);
          return { data: [] };
        }),
      ]);

      // Extract data arrays, handling different response structures
      const customersData = Array.isArray(customersResponse?.data) ? customersResponse.data :
        Array.isArray(customersResponse) ? customersResponse : [];
      const locationsData = Array.isArray(locationsResponse?.data) ? locationsResponse.data :
        Array.isArray(locationsResponse) ? locationsResponse : [];
      const productsRaw = productSizesResponse?.data;
      const productsData = Array.isArray(productsRaw) ? productsRaw :
        Array.isArray(productsRaw?.items) ? productsRaw.items :
        Array.isArray(productSizesResponse) ? productSizesResponse : [];
      const warehousesData = Array.isArray(warehousesResponse?.data) ? warehousesResponse.data :
        Array.isArray(warehousesResponse) ? warehousesResponse : [];

      console.log('Loaded dropdown data:', {
        customers: customersData.length,
        locations: locationsData.length,
        products: productsData.length,
        warehouses: warehousesData.length
      });

      const suppliersData = customersData.filter(c => String(c.customer_type ?? c.type ?? c.customerType ?? '').toLowerCase() === 'supplier');

      // Update state
      setCustomers(customersData);
      setSuppliers(suppliersData);
      setLocations(locationsData);
      setProducts(productsData);

      // Cache the data
      cache.customers = customersData;
      cache.suppliers = suppliersData;
      cache.locations = locationsData;
      cache.products = productsData;
      cache.warehouses = warehousesData;
      cache.loaded = true;

    } catch (error) {
      console.error('Error loading dropdown data:', error);
      // Set empty arrays on error to prevent undefined issues
      setCustomers([]);
      setLocations([]);
      setProducts([]);
      setWarehouses([]);
    } finally {
      cache.loading = false;
      setDropdownsLoading(false);
    }
  }, [cache, suppliers.length, locations.length, warehouses.length]);

  useEffect(() => {
    if (isOpen) loadDropdowns();
  }, [isOpen, loadDropdowns]);

  // If in edit mode, inject nested objects from `inquiry` into local lists and cache.
  // IMPORTANT: do NOT fetch single resources by id here — avoid extra API calls when the
  // parent `inquiry` payload already contains the nested objects. This prevents
  // GET /customers/:id (and similar) from being called when opening the edit form.
  useEffect(() => {
    const activeInquiry = currentInquiry || inquiry;
    if (!activeInquiry) return;

    // Helper to prepend into cache and state only if not already present in cache
    const prependIfMissing = (listName, item) => {
      if (!item || !item.id) return;
      const cached = cache[listName] || [];
      if (cached.some((c) => String(c.id) === String(item.id))) return;
      // update cache and state
      cache[listName] = [item, ...cached];
      if (listName === 'customers') setCustomers(prev => [item, ...prev]);
      if (listName === 'locations') setLocations(prev => [item, ...prev]);
      if (listName === 'products') setProducts(prev => [item, ...prev]);
    };

    // Customer: only inject if activeInquiry.customer object exists (do NOT fetch by id)
    if (activeInquiry.customer && typeof activeInquiry.customer === 'object') {
      const cust = { id: activeInquiry.customer_id, ...(activeInquiry.customer) };
      prependIfMissing('customers', cust);
    } else if (activeInquiry.customer_id && (activeInquiry.customer_name || activeInquiry.customer_company)) {
      const cust = { 
        id: activeInquiry.customer_id, 
        owner_name: activeInquiry.customer_name || undefined, 
        company_name: activeInquiry.customer_company || undefined 
      };
      prependIfMissing('customers', cust);
    }

    // Location: prefer activeInquiry.location object or top-level city/delivery_location values
    if (activeInquiry.location && typeof activeInquiry.location === 'object') {
      const loc = { id: activeInquiry.location_id, ...(activeInquiry.location) };
      prependIfMissing('locations', loc);
    } else if (activeInquiry.location_id && (activeInquiry.city || activeInquiry.delivery_location)) {
      const loc = { id: activeInquiry.location_id, city: activeInquiry.city || undefined, delivery_location: activeInquiry.delivery_location || undefined };
      prependIfMissing('locations', loc);
    }

    // Product size: inject from top-level product_size object if present
    if (activeInquiry.product_size && typeof activeInquiry.product_size === 'object') {
      const ps = { id: activeInquiry.product_size_id, ...(activeInquiry.product_size) };
      prependIfMissing('products', ps);
    }
    // Inject products from items array (inquiry response includes product info per item)
    if (Array.isArray(activeInquiry.items)) {
      activeInquiry.items.forEach(item => {
        if (item.product_size_id) {
          prependIfMissing('products', {
            id: item.product_size_id,
            alias_name: item.product_alias || item.alias_name,
            product_name: item.product_name,
            category_name: item.category_name,
          });
        }
      });
    }
    // Intentionally depend only on `inquiry` and `currentInquiry` so this runs once when opening/editing a record.
  }, [inquiry, currentInquiry, cache]);

  const handleOrderChange = (e) => {
    const { name, value, type, checked } = e.target;
    let v = value;
    if (type === 'checkbox') v = checked;
    if (['freight', 'loading_cost_per_kg'].includes(name)) v = sanitizeNumber(v);
    setOrder(prev => {
      const next = { ...prev, [name]: v };
      if (name === 'customer_id' && v) {
        const selectedCustomer = customers.find(c => String(c.id) === String(v));
        if (selectedCustomer) {
          if (Array.isArray(selectedCustomer.warehouses)) {
            const defaultWh = selectedCustomer.warehouses.find(w => w.is_default);
            if (defaultWh && defaultWh.location_id) {
              next.location_id = defaultWh.location_id;
            }
          }
          if (selectedCustomer.reference_party_name) {
            next.reference_party_name = selectedCustomer.reference_party_name;
          }
        }
      }
      return next;
    });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleItemChange = (index, field, value) => {
    const newVal = ['quantity', 'price', 'length', 'quantity_in_pieces'].includes(field) ? sanitizeNumber(value) : value;

    // Side effects live outside the state updater so React StrictMode's
    // double-invocation of the reducer can't fire duplicate requests.
    if (field === 'product_catalogue_id' && newVal) {
      loadProductsForSubCat(index, newVal);
    }
    if (field === 'product_size_id' && newVal) {
      hydrateProductDetail(index, newVal);
    }

    setOrder(prev => {
      const items = Array.isArray(prev.items) ? [...prev.items] : [];
      const updatedItem = {
        ...items[index],
        [field]: newVal
      };

      // If Sub Category changed, drop the current product selection so the user
      // picks a fresh one from the filtered list.
      if (field === 'product_catalogue_id') {
        updatedItem.product_size_id = '';
        updatedItem.product_name = '';
        updatedItem.size_name = '';
        updatedItem.length = '';
      }

      // Product-linked side effects: seed row length internally from the
      // product's standard_length, back-fill catalogue link (edit flow), and
      // capture product names for validation messages. Length is NOT shown in
      // the UI; it's stored in state so the save payload continues to work.
      const product = products.find(p => String(p.id) === String(updatedItem.product_size_id));
      if (product) {
        if (field === 'product_size_id') {
          const sLen = pickStandardLength(product);
          if (sLen > 0) updatedItem.length = String(sLen);
          // Auto-populate Sub Category from the picked product.
          const catId = resolveCatalogueId(product, subCategories);
          if (catId) updatedItem.product_catalogue_id = catId;
          // Only the by-id detail carries gst_rate; the list serializer omits
          // it. hydrateProductDetail fills it in a moment later.
          if (product.gst_rate !== undefined && product.gst_rate !== null) {
            updatedItem.gst_rate = product.gst_rate;
          }
        }
        updatedItem.product_name = product.product_name || product.name || updatedItem.product_name || '';
        updatedItem.size_name = product.alias_name || product.size_name || updatedItem.size_name || '';
      }

      items[index] = updatedItem;

      // Auto-add next line if this is the last item and all mandatory fields are filled (Add mode only)
      const isAddMode = !inquiry && !currentInquiry;
      if (isAddMode && index === items.length - 1) {
        const isFilled = updatedItem.product_size_id &&
                         updatedItem.quantity &&
                         updatedItem.price &&
                         updatedItem.units &&
                         parseFloat(updatedItem.quantity) > 0;

        if (isFilled) {
          items.push(emptyItem());
        }
      }

      return { ...prev, items };
    });

    // Clear item-level validation error for this field when user fills valid data
    const errorKey = `items.${index}.${field}`;
    if (field === 'product_size_id') {
      if (newVal) setErrors(prev => ({ ...prev, [errorKey]: null }));
    } else if (['quantity', 'price', 'quantity_in_pieces'].includes(field)) {
      const num = Number(newVal);
      if (newVal !== '' && !Number.isNaN(num)) {
        if (field === 'quantity' || field === 'quantity_in_pieces') {
          if (num > 0) setErrors(prev => ({ ...prev, [errorKey]: null }));
        } else {
          // price can be zero or positive
          if (num >= 0) setErrors(prev => ({ ...prev, [errorKey]: null }));
        }
      }
    } else {
      if (newVal !== '' && newVal !== null && typeof newVal !== 'undefined') setErrors(prev => ({ ...prev, [errorKey]: null }));
    }
  };

  const addItem = () => {
    const items = Array.isArray(order.items) ? [...order.items] : [];
    const lastIndex = items.length - 1;

    // Use centralized validator for order items from validateUtils
    const itemErrors = validateUtils.validateInquiryItems(items);

    // If there are any item errors, check if they concern the last item or overall items presence
    if (itemErrors && Object.keys(itemErrors).length > 0) {
      // If error is for the collection or for the last item, prevent adding
      const relevant = Object.keys(itemErrors).some(k => k === 'items' || k.startsWith(`items.${lastIndex}`));
      if (relevant) {
        setErrors(prev => ({ ...prev, ...itemErrors }));
        return; // don't add new item until previous is complete
      }
    }

    setOrder(prev => ({ ...prev, items: [...(prev.items || []), emptyItem()] }));
  };
  const removeItem = (idx) => {
    setOrder(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
    // Remove any errors tied to this item index
    setErrors(prev => {
      const next = {};
      Object.keys(prev || {}).forEach(k => {
        if (!k.startsWith(`items.${idx}`)) next[k] = prev[k];
      });
      return next;
    });
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === 'phone_number_1') {
      // Limit to 10 digits and allow only numeric characters
      newValue = value.replace(/\D/g, '').slice(0, 10);
    }

    setNewCustomer(prev => ({ ...prev, [name]: newValue }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validateStep = () => {
    console.log('Validating step - mode:', mode, 'step:', step);
    let v = {};

    try {
      if (mode === 'existing') {
        v = validateUtils.validateExistingCustomerInquiry(order || {});
      } else if (mode === 'new') {
        if (step === 0 || step === 1) {
          v = validateUtils.validateNewCustomerData(newCustomer || {});
        } else {
          v = validateUtils.validateNewCustomerInquiry(order || {}, newCustomer || {});
        }
      }
    } catch (err) {
      console.error('Error running centralized validators:', err);
      v = v || {};
    }

    setErrors(v);

    // If converting, perform additional manual validation for conversion fields
    if (isConverting && (mode === 'existing' || (mode === 'new' && step === 2))) {
      if (!order.source_type) {
        v.source_type = strings.validation?.required || 'Required';
      } else {
        if (order.source_type === 'warehouse' && !order.selected_warehouse_id) {
          v.selected_warehouse_id = strings.validation?.required || 'Required';
        }
        if (order.source_type === 'supplier' && !order.selected_supplier_id) {
          v.selected_supplier_id = strings.validation?.required || 'Required';
        }
      }
      setErrors(v);
    }

    return Object.keys(v).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep()) return;
    if (step === 0 && mode === 'new') {
      setStep(1); // Go to Warehouse step
    } else if (step === 1 && mode === 'new') {
      if (newCustomerWarehouses.length > 0 && !order.location_id) {
        setOrder(prev => ({ ...prev, location_id: 'new_wh_0' }));
      }
      setStep(2); // Go to Order Info step from Warehouse step
    } else {
      setStep(1); // For existing customer, go to Order Info (Step 1)
    }
  };

  const handleBack = () => setStep((prev) => Math.max(0, prev - 1));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;            // guard double-submit
    console.log('Submit handler called - mode:', mode, 'step:', step);

    if (!validateStep()) {
      console.log('Validation failed, not submitting');
      return;
    }

    console.log('Validation passed, building payload...');

    setSubmitting(true);
    try {
      // Availability of each product in the selected warehouse is validated by
      // the backend on submit (it receives warehouse_id + product_size_ids). The
      // former client-side pre-check compared against selectedWarehouse.items,
      // which the /warehouses list never returns, so it flagged every item as
      // unavailable regardless of stock — removed.

      // Build payload
      if (mode === 'existing') {
        const payload = {
          customer_id: Number(order.customer_id),
          location_id: Number(order.location_id) || 0,
          freight: (order.freight !== '' && order.freight !== null) ? parseFloat(order.freight) : 0,
          freight_type: order.freight_type || null,
          freight_pricing_type: order.freight_pricing_type || 'fixed',
          loading_cost_per_kg: (order.loading_cost_per_kg !== '' && order.loading_cost_per_kg !== null) ? parseFloat(order.loading_cost_per_kg) : 0,
          expected_delivery_date: order.expected_delivery_date || null,
          include_gst: !!order.include_gst,
          remark: order.remark || '',
          items: (order.items || []).filter(item => item.product_size_id).map(item => ({
            product_size_id: Number(item.product_size_id),
            units: item.units || null,
            length: (item.length !== '' && item.length !== null) ? parseFloat(item.length) : 0,
            quantity: (item.quantity !== '' && item.quantity !== null) ? parseFloat(item.quantity) : 0,
            quantity_in_pieces: (item.quantity_in_pieces !== '' && item.quantity_in_pieces !== null && item.quantity_in_pieces !== undefined) ? parseInt(item.quantity_in_pieces, 10) : 0,
            price: (item.price !== '' && item.price !== null) ? parseFloat(item.price) : 0,
            remark: item.remark || '',
          })),
        };

        if (isConverting) {
          payload.source_type = order.source_type;
          payload.warehouse_id = order.selected_warehouse_id ? Number(order.selected_warehouse_id) : null;
          payload.supplier_id = order.selected_supplier_id ? Number(order.selected_supplier_id) : null;
        }

        console.log('Existing customer payload:', payload);
        // Close ONLY on success — onSubmit returns {success:false} on failure
        // (it doesn't throw), so a failed save must not close and drop the data.
        const result = await onSubmit(payload);
        if (!result || result.success !== true) throw new Error((result && result.error) || 'Failed to submit inquiry');
        onClose();
      } else {
        // new customer: submit customer object and order together
        const payload = {
          customer: {
            ...newCustomer,
            credit_period: Number(newCustomer.credit_period),
          },
          warehouses: (newCustomerWarehouses || []).map(wh => ({
            name: wh.name || '',
            location_id: Number(wh.location_id) || 0,
            country: wh.country || 'India',
            state: wh.state || '',
            city: wh.city || '',
            address: wh.address || '',
            map_url: wh.map_url || '',
            contact_person: wh.contact_person || '',
            contact_phone: wh.contact_phone || ''
          })),
          location_id: Number(order.location_id) || 0,
          freight: (order.freight !== '' && order.freight !== null) ? parseFloat(order.freight) : 0,
          freight_type: order.freight_type || null,
          freight_pricing_type: order.freight_pricing_type || 'fixed',
          loading_cost_per_kg: (order.loading_cost_per_kg !== '' && order.loading_cost_per_kg !== null) ? parseFloat(order.loading_cost_per_kg) : 0,
          expected_delivery_date: order.expected_delivery_date || null,
          include_gst: !!order.include_gst,
          remark: order.remark || '',
          items: (order.items || []).filter(item => item.product_size_id).map(item => ({
            product_size_id: Number(item.product_size_id),
            units: item.units || null,
            length: (item.length !== '' && item.length !== null) ? parseFloat(item.length) : 0,
            quantity: (item.quantity !== '' && item.quantity !== null) ? parseFloat(item.quantity) : 0,
            quantity_in_pieces: (item.quantity_in_pieces !== '' && item.quantity_in_pieces !== null && item.quantity_in_pieces !== undefined) ? parseInt(item.quantity_in_pieces, 10) : 0,
            price: (item.price !== '' && item.price !== null) ? parseFloat(item.price) : 0,
            remark: item.remark || '',
          })),
        };

        if (isConverting) {
          payload.source_type = order.source_type;
          payload.warehouse_id = order.selected_warehouse_id ? Number(order.selected_warehouse_id) : null;
          payload.supplier_id = order.selected_supplier_id ? Number(order.selected_supplier_id) : null;
        }

        console.log('New customer payload:', payload);
        console.log('Calling onSubmit...');
        const result = await onSubmit(payload);
        if (!result || result.success !== true) throw new Error((result && result.error) || 'Failed to submit inquiry');
        console.log('onSubmit completed, closing form');
        onClose();
      }
    } catch (submitError) {
      console.error('Error submitting inquiry form:', submitError);
      // Keep the modal open and surface the error so the entered data isn't lost.
      const msg = submitError?.message || 'Failed to submit inquiry. Please try again.';
      setErrors(prev => ({ ...prev, submit: msg }));
    } finally {
      setSubmitting(false);
    }
  };

  // If the form isn't open, don't render UI
  if (!isOpen) return null;

  // Class used to hide native vertical scrollbar across browsers for the modal content
  const scrollClass = 'inquiry-form-scroll';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
      {/* Inline style block to target webkit scrollbar pseudo-element for this component scope */}
      <style>{`
        .${scrollClass} { -ms-overflow-style: none; scrollbar-width: none; }
        .${scrollClass}::-webkit-scrollbar { display: none; }
      `}</style>
      <div className={`w-full max-w-[95vw] 2xl:max-w-[1600px] relative bg-white rounded-2xl shadow-2xl max-h-[95vh] flex flex-col overflow-hidden ${scrollClass}`} style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{isConverting ? strings.title.convert : (currentInquiry || inquiry ? strings.title.edit : strings.title.add)}</h2>
          <button onClick={onClose} className="p-2 rounded-lg" style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 relative">
          {submitting && (
            <div className="absolute inset-0 z-40 flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.7)' }}>
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} spin className="text-4xl mb-4" style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }} />
                <p className="text-lg" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{isConverting ? strings.loading?.converting : strings.loading?.submitting}</p>
              </div>
            </div>
          )}
          {inquiryLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} spin className="text-4xl mb-4" style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }} />
                <p className="text-lg" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{strings.loading?.loadingDetails}</p>
              </div>
            </div>
          ) : (
            <>
              <div className={`flex-1 overflow-y-auto p-6 space-y-6 ${scrollClass}`}>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.mode.label}</label>
                  {/* If editing an inquiry, hide the 'new customer' option and lock to existing mode */}
                  {!(currentInquiry || inquiry) ? (
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2"><input type="radio" name="mode" checked={mode === 'existing'} onChange={() => setMode('existing')} /> {fields.mode.options.existing}</label>
                      <label className="flex items-center gap-2"><input type="radio" name="mode" checked={mode === 'new'} onChange={() => setMode('new')} /> {fields.mode.options.new}</label>
                    </div>
                  ) : (
                    <div className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{fields.mode.options.existing}</div>
                  )}
                </div>

                {mode === 'existing' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer_id.label} {REQUIRED}</label>
                        <SearchableSelect
                          name="customer_id"
                          value={order.customer_id}
                          onChange={handleOrderChange}
                          options={customers.map(c => ({ value: c.id, label: c.tally_name || c.company_name || c.owner_name || c.name || `Customer ${c.id}` }))}
                          placeholder={dropdownsLoading ? 'Loading customers...' : fields.customer_id.placeholder || 'Select a customer'}
                          disabled={isConverting || dropdownsLoading}
                          error={errors.customer_id}
                          onSearchChange={handleCustomerSearch}
                          loading={customerSearchLoading}
                          noOptionsMessage="Customer not found"
                        />
                        {errors.customer_id && (
                          <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.customer_id}</div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.location_id.label} {REQUIRED}</label>
                        <SearchableSelect
                          name="location_id"
                          value={order.location_id}
                          onChange={handleOrderChange}
                          options={locations.map(l => ({ value: l.id, label: l.delivery_location || l.city || l.name || `Location ${l.id}` }))}
                          placeholder={dropdownsLoading ? 'Loading locations...' : fields.location_id.placeholder || 'Select a location'}
                          disabled={isConverting || dropdownsLoading}
                          error={errors.location_id}
                          onSearchChange={handleLocationSearch}
                          loading={locationSearchLoading}
                          onAddNew={(term) => { setLocationAddInitialValue(term); setShowAddLocationModal(true); }}
                        />
                        {errors.location_id && (
                          <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.location_id}</div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.freightPricingType')}</label>
                        <SearchableSelect
                          options={[
                            { value: 'fixed', label: t('commonCols.statusOptions.fixed') },
                            { value: 'per_kg', label: t('commonCols.perKg') },
                          ]}
                          name="freight_pricing_type"
                          value={order.freight_pricing_type}
                          onChange={handleOrderChange}
                          placeholder={t('common.formFields.placeholders.selectFreightPricingType')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.freightType')}</label>
                        <select name="freight_type" value={order.freight_type} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}>
                          <option value="">{t('common.formFields.placeholders.selectFreightType')}</option>
                          <option value="ex_warehouse">{t('common.formFields.options.freightType.exWarehouse')}</option>
                          <option value="free_delivery_by_supplier">{t('common.formFields.options.freightType.freeDelivery')}</option>
                          <option value="freight_chargeable">{t('common.formFields.options.freightType.freightChargeable')}</option>
                          <option value="free_delivery_freight_to_pay">{t('common.formFields.options.freightType.freeDeliveryFreightToPay')}</option>
                          <option value="free_delivery_paid_by_company">{t('common.formFields.options.freightType.freeDeliveryPaidByCompany')}</option>
                        </select>
                      </div>
                      <div>
                        {order.freight_type !== 'free_delivery_by_supplier' && (
                          <>
                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                              {fields.freight.label}
                              {['ex_warehouse', 'free_delivery_paid_by_company'].includes(order.freight_type) && <span className="ml-1 text-xs font-normal text-gray-400">(Reference Only)</span>}
                            </label>
                            <input type="text" name="freight" value={order.freight} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.freight.placeholder} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.freight ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                            {errors.freight && (
                              <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.freight}</div>
                            )}
                          </>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.expected_delivery_date.label}</label>
                        <input type="date" lang="en-GB" name="expected_delivery_date" min={new Date().toLocaleDateString('en-CA')} value={order.expected_delivery_date} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.expected_delivery_date ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                        {errors.expected_delivery_date && (
                          <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.expected_delivery_date}</div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="flex items-center gap-2">
                          <input type="checkbox" name="include_gst" checked={!!order.include_gst} onChange={handleOrderChange} />
                          {fields.include_gst.label}
                        </label>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.referenceParty')}</label>
                        <input name="reference_party_name" value={order.reference_party_name} onChange={handleOrderChange} placeholder={t('common.formFields.placeholders.referenceParty')} className="w-full px-4 py-3 rounded-xl border-2" />
                      </div>
                    </div>

                    {isConverting && (
                      <div className="p-4 rounded-xl border-2 space-y-4" style={{ borderColor: UI_COLORS.BRAND.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.02) }}>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.sourceType.label} {REQUIRED}</label>
                            <select name="source_type" value={order.source_type} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.source_type ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}>
                              <option value="">{fields.sourceType.placeholder}</option>
                              <option value="warehouse">{fields.sourceType.options.warehouse.label}</option>
                              <option value="supplier">{fields.sourceType.options.supplier.label}</option>
                            </select>
                            {errors.source_type && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.source_type}</p>}
                          </div>
                          <div>
                            {order.source_type === 'warehouse' ? (
                              <>
                                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.warehouse_id.label} {REQUIRED}</label>
                                <SearchableSelect
                                  name="selected_warehouse_id"
                                  value={order.selected_warehouse_id}
                                  onChange={handleOrderChange}
                                  options={warehouses.map(w => ({
                                    value: w.id,
                                    label: `${w.name || w.city} ${w.is_active ? '🟢' : '🔴'}`
                                  }))}
                                  placeholder={fields.warehouse_id.placeholder}
                                  error={errors.selected_warehouse_id}
                                  onSearchChange={handleWarehouseSearch}
                                  loading={warehouseSearchLoading}
                                />
                                {errors.selected_warehouse_id && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.selected_warehouse_id}</p>}
                              </>
                            ) : order.source_type === 'supplier' ? (
                              <>
                                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.supplier_id.label} {REQUIRED}</label>
                                <SearchableSelect
                                  name="selected_supplier_id"
                                  value={order.selected_supplier_id}
                                  onChange={handleOrderChange}
                                  options={suppliers.map(s => ({ value: s.id, label: s.company_name || s.owner_name || s.name }))}
                                  placeholder={fields.supplier_id.placeholder}
                                  error={errors.selected_supplier_id}
                                  onSearchChange={handleSupplierSearch}
                                  loading={supplierSearchLoading}
                                />
                                {errors.selected_supplier_id && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.selected_supplier_id}</p>}
                              </>
                            ) : null}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.loading_cost_per_kg.label}</label>
                          <input name="loading_cost_per_kg" value={order.loading_cost_per_kg} onChange={handleOrderChange} placeholder={fields.loading_cost_per_kg.placeholder} className="w-full px-4 py-3 rounded-xl border-2" />
                        </div>

                      </div>
                    )}

                    {/* Items editor */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold">{fields.items?.label || 'Items'}</h4>
                        <label className="flex items-center gap-2 text-xs font-medium cursor-pointer" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                          <input type="checkbox" checked={showItemRemarks} onChange={(e) => setShowItemRemarks(e.target.checked)} />
                          {t('common.formFields.labels.showItemRemarks')}
                        </label>
                      </div>
                      {order.items.map((item, idx) => {
                        return (
                          <div key={idx} className="rounded-xl border p-3">
                            <div className="grid gap-3 items-end grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-[1.5fr_1.8fr_0.5fr_0.65fr_0.7fr_0.55fr_0.6fr_0.65fr_0.8fr_auto]">
                              <div className="sm:col-span-2 md:col-span-3 lg:col-span-1">
                                <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.subCategory') || 'Sub Category'}</label>
                                <SearchableSelect
                                  value={effectiveSubCatId(item, products, subCategories)}
                                  onChange={(e) => handleItemChange(idx, 'product_catalogue_id', e.target.value)}
                                  options={subCategories.map(sc => ({ value: sc.id, label: sc.product_name || sc.name || sc.alias_name || `#${sc.id}` }))}
                                  placeholder={t('common.formFields.placeholders.selectDefault')}
                                />
                              </div>
                              <div className="sm:col-span-2 md:col-span-3 lg:col-span-1">
                                <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.product_size_id.label} {REQUIRED}</label>
                                <SearchableSelect
                                  value={item.product_size_id}
                                  onChange={(e) => handleItemChange(idx, 'product_size_id', e.target.value)}
                                  options={(() => {
                                    const currentSubCat = effectiveSubCatId(item, products, subCategories);
                                    const all = products || [];
                                    const results = currentSubCat
                                      ? all.filter(p => resolveCatalogueId(p, subCategories) === currentSubCat)
                                      : all;
                                    const currentId = String(item.product_size_id);
                                    if (!currentId) return results.map(p => ({ value: p.id, label: p.alias_name || p.product_name || p.name }));

                                    const isIdInResults = results.some(p => String(p.id) === currentId);
                                    if (isIdInResults) return results.map(p => ({ value: p.id, label: p.alias_name || p.product_name || p.name }));

                                    const currentItem = (cache.products || []).find(p => String(p.id) === currentId);

                                    const rowOptions = currentItem ? [currentItem, ...results] : results;
                                    return rowOptions.map(p => ({
                                      value: p.id || p.product_size_id,
                                      label: (p.alias_name || p.product_name || p.name || p.size_name)
                                    }));
                                  })()}
                                  placeholder={dropdownsLoading ? t('commonForm.loading') : t('commonForm.selectProduct')}
                                  error={errors[`items.${idx}.product_size_id`]}
                                  onSearchChange={(term) => handleProductSearch(idx, term, effectiveSubCatId(item, products, subCategories))}
                                  loading={productSearchLoading.has(idx)}
                                  noOptionsMessage="Item not found"
                                />
                                {errors[`items.${idx}.product_size_id`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.product_size_id`]}</p>}
                              </div>
                              <div>
                                <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.pieces')}</label>
                                <input value={item.quantity_in_pieces} onChange={(e) => handleItemChange(idx, 'quantity_in_pieces', e.target.value)} placeholder="0" className="w-full px-3 py-2 rounded-xl border-2" style={{ borderColor: errors[`items.${idx}.quantity_in_pieces`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                                {errors[`items.${idx}.quantity_in_pieces`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.quantity_in_pieces`]}</p>}
                              </div>
                              <div>
                                <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t(isLengthUnit(item.units) ? 'common.formFields.labels.perPieceLength' : 'common.formFields.labels.perPieceWeight')}</label>
                                <input value={computePerPiece(item, products)} readOnly tabIndex={-1} className="w-full px-3 py-2 rounded-xl border-2" style={{ backgroundColor: '#f3f4f6', borderColor: UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.TEXT.SECONDARY }} />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.approxQty') || 'Approx Qty'}</label>
                                <input value={computeApproxQty(item, products)} readOnly tabIndex={-1} className="w-full px-3 py-2 rounded-xl border-2" style={{ backgroundColor: '#f3f4f6', borderColor: UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.TEXT.SECONDARY }} />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.quantity.label} {REQUIRED}</label>
                                <input value={item.quantity} onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)} placeholder={fields.quantity.placeholder} className="w-full px-3 py-2 rounded-xl border-2" style={{ borderColor: errors[`items.${idx}.quantity`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                                {errors[`items.${idx}.quantity`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.quantity`]}</p>}
                              </div>
                              <div>
                                <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.units.label}</label>
                                <select value={item.units} onChange={(e) => handleItemChange(idx, 'units', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background }}>
                                  {Object.entries(t('common.unitOptions', { returnObjects: true })).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.price.label} {REQUIRED}</label>
                                <input value={item.price} onChange={(e) => handleItemChange(idx, 'price', e.target.value)} placeholder={fields.price.placeholder} className="w-full px-3 py-2 rounded-xl border-2" style={{ borderColor: errors[`items.${idx}.price`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                                {errors[`items.${idx}.price`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.price`]}</p>}
                              </div>
                              <div>
                                <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.amountExGst')}</label>
                                <input value={formatCurrency(lineSubtotal(item), { showSymbol: false })} readOnly tabIndex={-1} className="w-full px-3 py-2 rounded-xl border-2 text-right" style={{ backgroundColor: '#f3f4f6', borderColor: UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.TEXT.SECONDARY }} />
                              </div>
                              <div className="flex items-end pb-0.5 justify-end lg:justify-start col-span-full lg:col-span-1">
                                <button type="button" onClick={() => removeItem(idx)} className="p-2 rounded bg-red-50 text-red-600 hover:bg-red-100 transition-colors">
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </div>
                            {showItemRemarks && (
                            <div className="mt-2">
                              <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.remark.label}</label>
                              <input value={item.remark} onChange={(e) => handleItemChange(idx, 'remark', e.target.value)} placeholder={fields.remark.placeholder} className="w-full px-3 py-2 rounded-xl border-2 transition-all outline-none focus:border-yellow-400" style={{ backgroundColor: '#fef9c3', borderColor: UI_COLORS.FORMS.INPUT.border }} />
                            </div>
                            )}
                          </div>
                        );
                      })}
                      <div>
                        <button type="button" onClick={addItem} className="px-4 py-2 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}><FontAwesomeIcon icon={faPlus} className="mr-2" />{strings.buttons.addItem}</button>
                      </div>
                      {(() => {
                        const t_ = computeInquiryTotals(order, products);
                        // formatCurrency, not a local formatter — the view
                        // screen renders these same figures through it, so any
                        // other formatting would make the numbers appear to
                        // change the moment the inquiry is saved.
                        const money = (v) => formatCurrency(v || 0);
                        const Row = ({ label, value, strong }) => (
                          <div className="flex items-center justify-between text-sm">
                            <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>{label}</span>
                            <span className={strong ? 'font-bold' : 'font-semibold'} style={{ color: UI_COLORS.TEXT.PRIMARY }}>{money(value)}</span>
                          </div>
                        );
                        return (
                          <div className="mt-4 ml-auto w-full max-w-sm rounded-xl border p-4 space-y-2" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.06) }}>
                            <Row label={t('common.formFields.labels.subtotal')} value={t_.subtotal} />
                            <Row label={t('common.formFields.labels.gstAmount')} value={t_.gstAmount} />
                            {t_.freight !== 0 && <Row label={fields.freight?.label || 'Freight'} value={t_.freight} />}
                            {t_.loadingCost !== 0 && <Row label={t('common.formFields.labels.loadingCost')} value={t_.loadingCost} />}
                            <div className="pt-2 mt-1 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                              <Row label={t('common.formFields.labels.total')} value={t_.total} strong />
                            </div>
                            {t_.hasUnknownGstRate && (
                              <p className="text-[11px] leading-snug pt-1" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                {t('common.helpers.gstRatePending')}
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.remark.label}</label>
                      <textarea name="remark" value={order.remark} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.remark.placeholder} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.remark ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                      {errors.remark && (
                        <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.remark}</div>
                      )}
                    </div>

                    {errors.submit && (
                      <div className="mt-3 p-3 rounded-lg text-sm" style={{ backgroundColor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>{errors.submit}</div>
                    )}
                    <div className="flex justify-end gap-4 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                      <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>{strings.buttons.cancel}</button>
                      <button type="submit" disabled={loading || submitting} className="px-6 py-3 rounded-xl font-semibold" style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
                        <FontAwesomeIcon icon={faSave} className="mr-2" /> {strings.buttons.submit}
                      </button>
                    </div>
                  </div>
                )}

                {mode === 'new' && (
                  <div>
                    {step === 0 && (
                      <>
                        <h3 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{strings.steps.customerInfo}</h3>
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.owner_name.label} {REQUIRED}</label>
                              <input name="owner_name" value={newCustomer.owner_name} onChange={e => handleCustomerChange(e)} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.owner_name.placeholder} style={{ borderColor: errors.owner_name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.owner_name && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.owner_name}</div>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.company_name.label} {REQUIRED}</label>
                              <input name="company_name" value={newCustomer.company_name} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.company_name.placeholder} style={{ borderColor: errors.company_name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.company_name && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.company_name}</div>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.customer_type?.label || 'Customer Type'} {REQUIRED}</label>
                              <input name="customer_type" value={newCustomer.customer_type} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.customer_type?.placeholder || 'Customer'} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.customer_type ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.customer_type && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.customer_type}</div>
                              )}
                            </div>
                            <div />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.email.label} {REQUIRED}</label>
                              <input name="email" value={newCustomer.email} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.email.placeholder} style={{ borderColor: errors.email ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.email && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.email}</div>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.phone_number_1?.label || 'Mobile Number'} {REQUIRED}</label>
                              <input name="phone_number_1" value={newCustomer.phone_number_1} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.phone_number_1?.placeholder || 'Mobile Number'} style={{ borderColor: errors.phone_number_1 ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.phone_number_1 && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.phone_number_1}</div>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.referenceParty')}</label>
                                <input name="reference_party_name" value={newCustomer.reference_party_name} onChange={handleCustomerChange} placeholder={t('common.formFields.placeholders.referenceParty')} className="w-full px-4 py-3 rounded-xl border-2" />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.gst_number?.label || 'GST Number'} {REQUIRED}</label>
                              <input name="gst_number" value={newCustomer.gst_number} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.gst_number?.placeholder || 'GST Number'} style={{ borderColor: errors.gst_number ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.gst_number && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.gst_number}</div>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.contact_person?.label || 'Contact Person'} {REQUIRED}</label>
                              <input name="contact_person" value={newCustomer.contact_person} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.contact_person?.placeholder || 'Contact Person'} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.contact_person ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.contact_person && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.contact_person}</div>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.address_line1?.label || 'Address'} {REQUIRED}</label>
                              <textarea name="address_line1" value={newCustomer.address_line1} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.address_line1?.placeholder || 'Address'} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.address_line1 ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.address_line1 && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.address_line1}</div>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.state?.label || 'State'} {REQUIRED}</label>
                              <SearchableSelect
                                name="state"
                                value={newCustomer.state}
                                onChange={handleCustomerChange}
                                options={t('locations.states', { returnObjects: true })}
                                placeholder={fields.customer.state?.placeholder || 'Select State'}
                                error={errors.state}
                              />
                              {errors.state && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.state}</div>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.city?.label || 'City'} {REQUIRED}</label>
                              <input name="city" value={newCustomer.city} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.city?.placeholder || 'City'} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.city ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.city && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.city}</div>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.country?.label || 'Country'} {REQUIRED}</label>
                              <input name="country" value={newCustomer.country} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.country?.placeholder || 'Country'} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.country ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.country && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.country}</div>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.zip_code?.label || 'Zip Code'} {REQUIRED}</label>
                              <input name="zip_code" value={newCustomer.zip_code} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.zip_code?.placeholder || 'Zip Code'} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.zip_code ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.zip_code && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.zip_code}</div>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.tally_name?.label || 'Tally Name'}</label>
                              <input name="tally_name" value={newCustomer.tally_name} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.tally_name?.placeholder || 'Tally Name'} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.tally_name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.tally_name && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.tally_name}</div>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.credit_period?.label || 'Credit Period (days)'}</label>
                              <input name="credit_period" type="number" value={newCustomer.credit_period} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.credit_period?.placeholder || '30'} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.credit_period ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.credit_period && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.credit_period}</div>
                              )}
                            </div>
                          </div>
                          {/* minimal fields shown; more can be added similarly */}
                          <div className="flex justify-end gap-4 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                            <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>{strings.buttons.cancel}</button>
                            <button type="button" onClick={handleNext} className="px-6 py-3 rounded-xl font-semibold" style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
                              {strings.buttons.next} <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {step === 1 && mode === 'new' && (
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('common.formFields.labels.warehouseInformation')}</h3>
                          <button
                            type="button"
                            onClick={() => setShowWarehouseModal(true)}
                            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:scale-105"
                            style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.1), color: UI_COLORS.BRAND.PRIMARY }}
                          >
                            <FontAwesomeIcon icon={faPlus} className="mr-2" /> Add Warehouse
                          </button>
                        </div>

                        <p className="text-sm mb-4" style={{ color: UI_COLORS.TEXT.MUTED }}>
                          You can add one or more warehouses for this new customer now, or skip this step.
                        </p>

                        <div className="space-y-3 min-h-[200px]">
                          {newCustomerWarehouses.length === 0 ? (
                            <div className="flex flex-col items-center justify-center p-8 rounded-2xl border-2 border-dashed" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                              <FontAwesomeIcon icon={faWarehouse} className="text-3xl mb-2" style={{ color: UI_COLORS.TEXT.MUTED }} />
                              <p className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.MUTED }}>No warehouses added yet</p>
                              <p className="text-xs mt-1" style={{ color: UI_COLORS.TEXT.MUTED }}>Warehouses are optional</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {newCustomerWarehouses.map((wh, idx) => (
                                <div key={idx} className="p-4 rounded-xl border relative group" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.SECONDARY }}>
                                  <button
                                    type="button"
                                    onClick={() => setNewCustomerWarehouses(prev => {
                                      const filtered = prev.filter((_, i) => i !== idx);
                                      if (filtered.length > 0 && !filtered.some(w => w.is_default)) {
                                        filtered[0].is_default = true;
                                      }
                                      return filtered;
                                    })}
                                    className="absolute top-2 right-2 p-1.5 rounded-lg text-red-500 hover:bg-red-50 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-all"
                                  >
                                    <FontAwesomeIcon icon={faTrash} className="text-xs" />
                                  </button>
                                  <div className="flex items-start gap-3">
                                    <div className="mt-1 flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-500">
                                      <FontAwesomeIcon icon={faWarehouse} className="text-sm" />
                                    </div>
                                    <div className="overflow-hidden">
                                      <div className="flex items-center gap-2 overflow-hidden">
                                        <h4 className="font-semibold text-sm truncate" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{wh.name}</h4>
                                        {wh.is_default && (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-bold uppercase tracking-wider flex-shrink-0">Default</span>
                                        )}
                                      </div>
                                      <p className="text-xs mt-0.5 truncate" style={{ color: UI_COLORS.TEXT.MUTED }}>{wh.address || wh.city || 'No address provided'}</p>
                                      {!wh.is_default && (
                                        <button
                                          type="button"
                                          onClick={() => setNewCustomerWarehouses(prev => prev.map((w, i) => ({ ...w, is_default: i === idx })))}
                                          className="text-[10px] mt-1 text-blue-600 hover:underline font-semibold"
                                        >
                                          Set as Default
                                        </button>
                                      )}
                                      {wh.contact_person && (
                                        <p className="text-[10px] mt-1 font-medium" style={{ color: UI_COLORS.BRAND.PRIMARY }}>
                                          Contact: {wh.contact_person}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-4 mt-8 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                          <button type="button" onClick={handleBack} className="px-6 py-3 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>
                            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> {strings.buttons.back}
                          </button>
                          <button type="button" onClick={handleNext} className="ml-auto px-6 py-3 rounded-xl font-semibold" style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
                            {newCustomerWarehouses.length === 0 ? t('commonForm.skipAndNext') : strings.buttons.next} <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                          </button>
                        </div>
                      </>
                    )}

                    {((step === 1 && mode === 'existing') || (step === 2 && mode === 'new')) && (
                      <>
                        <h3 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{strings.steps.orderInfo}</h3>
                        {(newCustomer.owner_name || newCustomer.company_name) && (
                          <div className="flex items-center gap-4 px-4 py-2 rounded-lg text-sm" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.06) }}>
                            {newCustomer.owner_name && (
                              <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>Customer: <span className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{newCustomer.owner_name}</span></span>
                            )}
                            {newCustomer.company_name && (
                              <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>Company: <span className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{newCustomer.company_name}</span></span>
                            )}
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.location_id.label} {REQUIRED}</label>
                            <SearchableSelect
                              name="location_id"
                              value={order.location_id}
                              onChange={handleOrderChange}
                              options={[
                                ...(mode === 'new' && newCustomerWarehouses.length > 0
                                  ? newCustomerWarehouses.map((wh, idx) => ({ value: `new_wh_${idx}`, label: `${wh.name || wh.city} (Warehouse)` }))
                                  : []),
                                ...locations.map(l => ({ value: l.id, label: l.delivery_location || l.city || l.name || l.company_name || l.location || `Location ${l.id}` }))
                              ]}
                              placeholder={dropdownsLoading ? 'Loading locations...' : fields.location_id.placeholder || 'Select a location'}
                              disabled={dropdownsLoading}
                              error={errors.location_id}
                              onSearchChange={handleLocationSearch}
                              loading={locationSearchLoading}
                              onAddNew={(term) => { setLocationAddInitialValue(term); setShowAddLocationModal(true); }}
                            />
                            {errors.location_id && (
                              <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.location_id}</div>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.expected_delivery_date.label}</label>
                            <input type="date" name="expected_delivery_date" min={new Date().toLocaleDateString('en-CA')} value={order.expected_delivery_date} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.expected_delivery_date ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                            {errors.expected_delivery_date && (
                              <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.expected_delivery_date}</div>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.freightPricingType')}</label>
                            <SearchableSelect
                              options={[
                                { value: 'fixed', label: t('commonCols.statusOptions.fixed') },
                                { value: 'per_kg', label: t('commonCols.perKg') },
                              ]}
                              name="freight_pricing_type"
                              value={order.freight_pricing_type}
                              onChange={handleOrderChange}
                              placeholder={t('common.formFields.placeholders.selectFreightPricingType')}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.freightType')}</label>
                            <select name="freight_type" value={order.freight_type} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}>
                              <option value="">{t('common.formFields.placeholders.selectFreightType')}</option>
                              <option value="ex_warehouse">{t('common.formFields.options.freightType.exWarehouse')}</option>
                              <option value="free_delivery_by_supplier">{t('common.formFields.options.freightType.freeDelivery')}</option>
                              <option value="freight_chargeable">{t('common.formFields.options.freightType.freightChargeable')}</option>
                              <option value="free_delivery_freight_to_pay">{t('common.formFields.options.freightType.freeDeliveryFreightToPay')}</option>
                              <option value="free_delivery_paid_by_company">{t('common.formFields.options.freightType.freeDeliveryPaidByCompany')}</option>
                            </select>
                          </div>
                          <div>
                            <label className="flex items-center gap-2">
                              <input type="checkbox" name="include_gst" checked={!!order.include_gst} onChange={handleOrderChange} />
                              {fields.include_gst.label}
                            </label>
                          </div>
                          <div />
                        </div>
                        {order.freight_type !== 'free_delivery_by_supplier' && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                {fields.freight.label}
                                {['ex_warehouse', 'free_delivery_paid_by_company'].includes(order.freight_type) && <span className="ml-1 text-xs font-normal text-gray-400">(Reference Only)</span>}
                              </label>
                              <input name="freight" value={order.freight} onChange={handleOrderChange} placeholder={fields.freight.placeholder} className="w-full px-4 py-3 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.freight ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.freight && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.freight}</div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Items editor for new customer flow */}
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold">{fields.items?.label || 'Items'}</h4>
                            <label className="flex items-center gap-2 text-xs font-medium cursor-pointer" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                              <input type="checkbox" checked={showItemRemarks} onChange={(e) => setShowItemRemarks(e.target.checked)} />
                              {t('common.formFields.labels.showItemRemarks')}
                            </label>
                          </div>
                          {order.items.map((item, idx) => {

                            return (
                              <div key={idx} className="rounded-xl border p-3">
                                <div className="grid gap-3 items-end grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-[1.5fr_1.8fr_0.5fr_0.65fr_0.7fr_0.55fr_0.6fr_0.65fr_0.8fr_auto]">
                                  <div className="sm:col-span-2 md:col-span-3 lg:col-span-1">
                                    <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.subCategory') || 'Sub Category'}</label>
                                    <SearchableSelect
                                      value={effectiveSubCatId(item, products, subCategories)}
                                      onChange={(e) => handleItemChange(idx, 'product_catalogue_id', e.target.value)}
                                      options={subCategories.map(sc => ({ value: sc.id, label: sc.product_name || sc.name || sc.alias_name || `#${sc.id}` }))}
                                      placeholder={t('common.formFields.placeholders.selectDefault')}
                                    />
                                  </div>
                                  <div className="sm:col-span-2 md:col-span-3 lg:col-span-1">
                                    <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.product_size_id.label} {REQUIRED}</label>
                                    <SearchableSelect
                                      value={item.product_size_id}
                                      onChange={(e) => handleItemChange(idx, 'product_size_id', e.target.value)}
                                      options={(() => {
                                        const currentSubCat = effectiveSubCatId(item, products, subCategories);
                                        const all = products || [];
                                        const results = currentSubCat
                                          ? all.filter(p => resolveCatalogueId(p, subCategories) === currentSubCat)
                                          : all;
                                        const currentId = String(item.product_size_id);
                                        if (!currentId) return results.map(p => ({ value: p.id, label: p.alias_name || p.product_name || p.name }));

                                        const isIdInResults = results.some(p => String(p.id) === currentId);
                                        if (isIdInResults) return results.map(p => ({ value: p.id, label: p.alias_name || p.product_name || p.name }));

                                        const currentItem = (cache.products || []).find(p => String(p.id) === currentId);

                                        const rowOptions = currentItem ? [currentItem, ...results] : results;
                                        return rowOptions.map(p => ({
                                          value: p.id || p.product_size_id,
                                          label: (p.alias_name || p.product_name || p.name || p.size_name)
                                        }));
                                      })()}
                                      placeholder={dropdownsLoading ? t('commonForm.loading') : t('commonForm.selectProduct')}
                                      error={errors[`items.${idx}.product_size_id`]}
                                      onSearchChange={(term) => handleProductSearch(idx, term, effectiveSubCatId(item, products, subCategories))}
                                      loading={productSearchLoading.has(idx)}
                                    />
                                    {errors[`items.${idx}.product_size_id`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.product_size_id`]}</p>}
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.pieces')}</label>
                                    <input value={item.quantity_in_pieces} onChange={(e) => handleItemChange(idx, 'quantity_in_pieces', e.target.value)} placeholder="0" className="w-full px-3 py-2 rounded-xl border-2" style={{ borderColor: errors[`items.${idx}.quantity_in_pieces`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                                    {errors[`items.${idx}.quantity_in_pieces`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.quantity_in_pieces`]}</p>}
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t(isLengthUnit(item.units) ? 'common.formFields.labels.perPieceLength' : 'common.formFields.labels.perPieceWeight')}</label>
                                    <input value={computePerPiece(item, products)} readOnly tabIndex={-1} className="w-full px-3 py-2 rounded-xl border-2" style={{ backgroundColor: '#f3f4f6', borderColor: UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.TEXT.SECONDARY }} />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.approxQty') || 'Approx Qty'}</label>
                                    <input value={computeApproxQty(item, products)} readOnly tabIndex={-1} className="w-full px-3 py-2 rounded-xl border-2" style={{ backgroundColor: '#f3f4f6', borderColor: UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.TEXT.SECONDARY }} />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.quantity.label} {REQUIRED}</label>
                                    <input value={item.quantity} onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)} placeholder={fields.quantity.placeholder} className="w-full px-3 py-2 rounded-xl border-2" style={{ borderColor: errors[`items.${idx}.quantity`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                                    {errors[`items.${idx}.quantity`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.quantity`]}</p>}
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.units.label}</label>
                                    <select value={item.units} onChange={(e) => handleItemChange(idx, 'units', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background }}>
                                      {Object.entries(t('common.unitOptions', { returnObjects: true })).map(([key, label]) => (
                                        <option key={key} value={key}>{label}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.price.label} {REQUIRED}</label>
                                    <input value={item.price} onChange={(e) => handleItemChange(idx, 'price', e.target.value)} placeholder={fields.price.placeholder} className="w-full px-3 py-2 rounded-xl border-2" style={{ borderColor: errors[`items.${idx}.price`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                                    {errors[`items.${idx}.price`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.price`]}</p>}
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.amountExGst')}</label>
                                    <input value={formatCurrency(lineSubtotal(item), { showSymbol: false })} readOnly tabIndex={-1} className="w-full px-3 py-2 rounded-xl border-2 text-right" style={{ backgroundColor: '#f3f4f6', borderColor: UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.TEXT.SECONDARY }} />
                                      </div>
                                  <div className="flex items-end pb-0.5 justify-end lg:justify-start col-span-full lg:col-span-1">
                                    <button type="button" onClick={() => removeItem(idx)} className="p-2 rounded bg-red-50 text-red-600 hover:bg-red-100 transition-colors">
                                      <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                  </div>
                                </div>
                                {showItemRemarks && (
                                <div className="mt-2">
                                  <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.remark.label}</label>
                                  <input value={item.remark} onChange={(e) => handleItemChange(idx, 'remark', e.target.value)} placeholder={fields.remark.placeholder} className="w-full px-3 py-2 rounded-xl border-2 transition-all outline-none focus:border-yellow-400" style={{ backgroundColor: '#fef9c3', borderColor: UI_COLORS.FORMS.INPUT.border }} />
                                </div>
                                )}
                              </div>
                            );
                          })}
                          <div>
                            <button type="button" onClick={addItem} className="px-4 py-2 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}><FontAwesomeIcon icon={faPlus} className="mr-2" />{strings.buttons.addItem}</button>
                          </div>
                          {(() => {
                            const t_ = computeInquiryTotals(order, products);
                            const money = (v) => formatCurrency(v || 0);
                            const Row = ({ label, value, strong }) => (
                              <div className="flex items-center justify-between text-sm">
                                <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>{label}</span>
                                <span className={strong ? 'font-bold' : 'font-semibold'} style={{ color: UI_COLORS.TEXT.PRIMARY }}>{money(value)}</span>
                              </div>
                            );
                            return (
                              <div className="mt-4 ml-auto w-full max-w-sm rounded-xl border p-4 space-y-2" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.06) }}>
                                <Row label={t('common.formFields.labels.subtotal')} value={t_.subtotal} />
                                <Row label={t('common.formFields.labels.gstAmount')} value={t_.gstAmount} />
                                {t_.freight !== 0 && <Row label={fields.freight?.label || 'Freight'} value={t_.freight} />}
                                {t_.loadingCost !== 0 && <Row label={t('common.formFields.labels.loadingCost')} value={t_.loadingCost} />}
                                <div className="pt-2 mt-1 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                  <Row label={t('common.formFields.labels.total')} value={t_.total} strong />
                                </div>
                                {t_.hasUnknownGstRate && (
                                  <p className="text-[11px] leading-snug pt-1" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                    {t('common.helpers.gstRatePending')}
                                  </p>
                                )}
                              </div>
                            );
                      })()}
                        </div>



                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.remark.label}</label>
                          <textarea name="remark" value={order.remark} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.remark.placeholder} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.remark ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.remark && (
                            <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.remark}</div>
                          )}
                        </div>

                        {errors.submit && (
                          <div className="mt-3 p-3 rounded-lg text-sm" style={{ backgroundColor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>{errors.submit}</div>
                        )}
                        <div className="flex items-center gap-4 mt-4">
                          <button type="button" onClick={handleBack} className="px-4 py-2 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>
                            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> {strings.buttons.back}
                          </button>
                          <div className="ml-auto flex items-center gap-4">
                            <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>{strings.buttons.cancel}</button>
                            <button type="submit" disabled={loading || submitting} className="px-6 py-3 rounded-xl font-semibold" style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>{isConverting ? strings.buttons.convert : strings.buttons.submit}</button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </form>

        <AddLocationModal
          isOpen={showAddLocationModal}
          onClose={() => { setShowAddLocationModal(false); setLocationAddInitialValue(''); }}
          onSuccess={handleAddLocationSuccess}
          initialValues={{ delivery_location: locationAddInitialValue }}
        />

        <CustomerWarehouseForm
          isOpen={showWarehouseModal}
          onClose={() => setShowWarehouseModal(false)}
          isOffline={true}
          onSave={async (data) => {
            let locationId = data.location_id;

            // If it's a new location (not selected from existing), create it via API
            if (!locationId) {
              try {
                const locRes = await locationsService.createLocation({
                  delivery_location: (data.address && data.address.trim().length >= 5) 
                    ? data.address.trim() 
                    : `${data.name} - ${data.address || data.city || ''}`.trim(),
                  city: data.city,
                  state: data.state,
                  country: data.country || 'India',
                  map_url: data.map_url,
                  is_active: true
                });

                if (locRes.success && locRes.data?.id) {
                  locationId = locRes.data.id;
                }
              } catch (err) {
                console.error("Failed to create location for warehouse:", err);
              }
            }

            const updatedWarehouse = { ...data, location_id: locationId };
            setNewCustomerWarehouses(prev => {
              const isFirst = prev.length === 0;
              const shouldBeDefault = isFirst || updatedWarehouse.is_default;
              const newWh = { ...updatedWarehouse, is_default: shouldBeDefault };
              if (shouldBeDefault) {
                return [...prev.map(w => ({ ...w, is_default: false })), newWh];
              }
              return [...prev, newWh];
            });

            // Automatically use this location as the delivery location for the inquiry
            if (locationId) {
              setOrder(prev => ({ ...prev, location_id: locationId }));
            }
          }}
        />
      </div>
    </div >
  );
};

export default InquiryForm;
